import React, { useEffect, useMemo, useState } from "react";
import BubbleResizeHandle from "./BubbleResizeHandle";
import TailDragHandle from "./TailDragHandle";
import { computeTailTriangle, normalizeTails } from "../utils/tailGeometry";
import { findBubbleShape } from "../constants/bubbleShapes";
import { computeSnap } from "../utils/snapAlign";
import { usePointerDrag } from "../hooks/usePointerDrag";
import { placementIntent } from "../utils/motion";
import { tailShape, defaultTailStyle } from "../utils/bubbleGeometry";
import { useUndoGesture } from "../hooks/useUndoGesture";

const MIN_WIDTH = 90;
const MIN_HEIGHT = 60;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const SpeechBubble = ({ bubble, comicStyle, cellSize, onChange, onSelect, isSelected, siblingRects, snapGridSize, snapEnabled = true, onGuidesChange, onDuplicate }) => {
  const styleBubble = comicStyle?.bubble || {};
  const [isHovered, setIsHovered] = useState(false);
  // A bubble placed by double-click opens straight into typing
  const [intent] = useState(() => placementIntent(bubble.id));
  const placing = Boolean(intent);
  const [isEditing, setIsEditing] = useState(Boolean(intent?.edit));
  const [draftText, setDraftText] = useState(bubble.text || "");

  useEffect(() => {
    setDraftText(bubble.text || "");
  }, [bubble.text]);

  const shapeDef = findBubbleShape(bubble.shape);
  const tailEnabled = bubble.tailEnabled && !shapeDef.noTail;

  // Normalize tails array
  const tails = useMemo(() => {
    if (!tailEnabled) return [];
    return normalizeTails(bubble);
  }, [tailEnabled, bubble]);

  const edges = useMemo(() => ["top", "bottom", "left", "right", "nw", "ne", "sw", "se"], []);

  // ─── Pointer drags: move the bubble, resize it, or steer a tail ──────────
  const handleResizeMove = ({ dx: deltaX, dy: deltaY, event, payload }) => {
    const { edge, startRect } = payload;
    let nextX = startRect.x;
    let nextY = startRect.y;
    let nextWidth = startRect.width;
    let nextHeight = startRect.height;

    if (["right", "se", "ne"].includes(edge)) {
      nextWidth = clamp(startRect.width + deltaX, MIN_WIDTH, cellSize.width - startRect.x);
    }

    if (["left", "sw", "nw"].includes(edge)) {
      const maxLeft = startRect.x + startRect.width - MIN_WIDTH;
      nextX = clamp(startRect.x + deltaX, 0, maxLeft);
      nextWidth = clamp(startRect.width - (nextX - startRect.x), MIN_WIDTH, startRect.width + startRect.x);
    }

    if (["bottom", "se", "sw"].includes(edge)) {
      nextHeight = clamp(startRect.height + deltaY, MIN_HEIGHT, cellSize.height - startRect.y);
    }

    if (["top", "nw", "ne"].includes(edge)) {
      const maxTop = startRect.y + startRect.height - MIN_HEIGHT;
      nextY = clamp(startRect.y + deltaY, 0, maxTop);
      nextHeight = clamp(startRect.height - (nextY - startRect.y), MIN_HEIGHT, startRect.height + startRect.y);
    }

    const { snappedRect, guides } = computeSnap({
      rect: { x: nextX, y: nextY, width: nextWidth, height: nextHeight },
      cellSize,
      siblings: siblingRects || [],
      gridSize: snapGridSize || 10,
      skipSnap: event.altKey || !snapEnabled,
    });

    const finalWidth = snappedRect.width || nextWidth;
    const finalHeight = snappedRect.height || nextHeight;
    const updates = { x: snappedRect.x, y: snappedRect.y, width: finalWidth, height: finalHeight };

    // Scale tail tip positions proportionally when bubble dimensions change
    if (payload.startTails.length > 0 && (finalWidth !== startRect.width || finalHeight !== startRect.height)) {
      const scaleX = startRect.width > 0 ? finalWidth / startRect.width : 1;
      const scaleY = startRect.height > 0 ? finalHeight / startRect.height : 1;
      const maxTipX = cellSize.width - snappedRect.x;
      const maxTipY = cellSize.height - snappedRect.y;
      updates.tails = payload.startTails.map((t) => ({
        ...t,
        tipX: clamp(t.tipX * scaleX, -snappedRect.x, maxTipX),
        tipY: clamp(t.tipY * scaleY, -snappedRect.y, maxTipY),
      }));
    }

    onChange(updates);
    onGuidesChange && onGuidesChange(guides);
  };

  const handleBodyMove = ({ dx, dy, event, payload }) => {
    const rawX = Math.max(0, Math.min(cellSize.width - bubble.width, payload.origX + dx));
    const rawY = Math.max(0, Math.min(cellSize.height - bubble.height, payload.origY + dy));

    const { snappedRect, guides } = computeSnap({
      rect: { x: rawX, y: rawY, width: bubble.width, height: bubble.height },
      cellSize,
      siblings: siblingRects || [],
      gridSize: snapGridSize || 10,
      skipSnap: event.altKey || !snapEnabled,
    });

    onChange({ x: snappedRect.x, y: snappedRect.y });
    onGuidesChange && onGuidesChange(guides);
  };

  const handleTailMove = ({ dx, dy, payload }) => {
    let nextTipX = payload.startTipX + dx;
    let nextTipY = payload.startTipY + dy;

    // Enforce tip stays outside the bubble rect
    const margin = 4;
    const insideX = nextTipX > margin && nextTipX < bubble.width - margin;
    const insideY = nextTipY > margin && nextTipY < bubble.height - margin;
    if (insideX && insideY) {
      const distTop = nextTipY - margin;
      const distBottom = (bubble.height - margin) - nextTipY;
      const distLeft = nextTipX - margin;
      const distRight = (bubble.width - margin) - nextTipX;
      const minDist = Math.min(distTop, distBottom, distLeft, distRight);
      if (minDist === distTop) nextTipY = -margin;
      else if (minDist === distBottom) nextTipY = bubble.height + margin;
      else if (minDist === distLeft) nextTipX = -margin;
      else nextTipX = bubble.width + margin;
    }

    const newTails = tails.map((t, i) =>
      i === payload.tailIndex ? { ...t, tipX: nextTipX, tipY: nextTipY } : t
    );
    onChange({ tails: newTails });
  };

  const drag = usePointerDrag({
    onMove: (move) => {
      if (move.payload.kind === "resize") handleResizeMove(move);
      else if (move.payload.kind === "tail") handleTailMove(move);
      else handleBodyMove(move);
    },
    onEnd: () => onGuidesChange && onGuidesChange([]),
  });
  const dragKind = drag.active?.kind || null;
  useUndoGesture(Boolean(dragKind));

  const showControls = isHovered || isSelected || isEditing || Boolean(dragKind);

  const startResize = (edge, event) => {
    if (bubble.locked) return;
    event.preventDefault();
    event.stopPropagation();
    drag.start(event, {
      kind: "resize",
      edge,
      startRect: { x: bubble.x, y: bubble.y, width: bubble.width, height: bubble.height },
      startTails: tails,
    });
  };

  const startTailDrag = (tailIndex, event) => {
    if (bubble.locked) return;
    event.preventDefault();
    event.stopPropagation();
    const tail = tails[tailIndex];
    drag.start(event, { kind: "tail", tailIndex, startTipX: tail.tipX, startTipY: tail.tipY });
  };

  // Compute tail triangles for all tails
  const tailTriangles = useMemo(() => {
    return tails.map((tail) => ({
      ...tail,
      triangle: computeTailTriangle(bubble.width, bubble.height, tail.tipX, tail.tipY, tail.width || 20),
    }));
  }, [tails, bubble.width, bubble.height]);

  const handleTextCommit = () => {
    setIsEditing(false);
    onChange({ text: draftText });
  };

  const shouldAllowOverflow = tailEnabled || showControls;
  const svgPadding = 200;

  // Font properties — fall back to the active comic style's lettering
  const fontSize = bubble.fontSize || 14;
  const fontFamily = bubble.fontFamily || styleBubble.fontFamily || "inherit";
  const fontWeight = bubble.fontWeight || styleBubble.fontWeight || "normal";
  const fontStyle = bubble.fontStyle || "normal";
  const textTransform = bubble.textTransform || styleBubble.textTransform || "none";
  const textAlign = bubble.textAlign || styleBubble.textAlign || "left";

  // Color properties — fall back to the active comic style's inks
  const bgColor = bubble.bgColor || shapeDef.fill || styleBubble.bgColor || "#ffffff";
  const borderColor = bubble.borderColor || styleBubble.borderColor || "#1a1a1a";
  const textColor = bubble.textColor || styleBubble.textColor || "#1f2937";
  const strokeW = bubble.borderWidth ?? styleBubble.borderWidth ?? 2;
  const opacity = bubble.opacity != null ? bubble.opacity : 1;

  // SVG bubble outline path
  const bubblePath = useMemo(
    () => shapeDef.getPath(bubble.width, bubble.height),
    [shapeDef, bubble.width, bubble.height]
  );

  // Tails come from shared geometry so exports draw exactly the same shapes
  const renderTail = (tailData, index) => {
    const { triangle, style } = tailData;
    if (!triangle) return null;
    const shape = tailShape(triangle, style || defaultTailStyle(shapeDef), strokeW);
    if (shape.circles.length) {
      return shape.circles.map((c, i) => (
        <circle
          key={`tail-${index}-circle-${i}`}
          cx={c.cx} cy={c.cy} r={c.r}
          fill={bgColor} stroke={borderColor} strokeWidth={1}
        />
      ));
    }
    return (
      <g key={`tail-${index}`}>
        <path d={shape.cover} fill={bgColor} stroke="none" />
        <path d={shape.fill} fill={bgColor} stroke="none" />
        <path d={shape.stroke} fill="none" stroke={borderColor} strokeWidth={strokeW} strokeLinejoin={shape.lineJoin} />
      </g>
    );
  };

  const isLifted = dragKind === "move" && drag.moving;

  return (
    <div
      className={`striply-lift${isLifted ? " is-lifted" : ""}${placing ? " striply-place" : ""}`}
      data-canvas-item="bubble"
      data-item-id={bubble.id}
      style={{
        position: "absolute",
        left: bubble.x,
        top: bubble.y,
        width: bubble.width,
        height: bubble.height,
        overflow: shouldAllowOverflow ? "visible" : "hidden",
        pointerEvents: "auto",
        zIndex: 9,
        outline: isSelected ? "2px solid #00a4e4" : undefined,
        outlineOffset: isSelected ? 3 : undefined,
        opacity,
        cursor: bubble.locked ? "default" : isEditing ? "text" : dragKind === "move" ? "grabbing" : "grab",
        touchAction: "none",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onPointerDown={(event) => {
        if (isEditing) return;
        // Skip if clicking on a resize handle or tail handle
        if (event.target.dataset?.resizeHandle || event.target.dataset?.tailHandle) return;
        event.preventDefault();
        event.stopPropagation();
        // Alt+click = duplicate
        if (event.altKey && onDuplicate) {
          onDuplicate();
          return;
        }
        onSelect && onSelect();
        if (bubble.locked) return;
        drag.start(event, { kind: "move", origX: bubble.x, origY: bubble.y });
      }}
      onClick={(event) => {
        event.stopPropagation();
        onSelect && onSelect();
      }}
      onFocusCapture={(event) => {
        event.stopPropagation();
        onSelect && onSelect();
      }}
      tabIndex={0}
    >
      {/* SVG bubble outline */}
      <svg
        style={{
          position: "absolute",
          left: -svgPadding,
          top: -svgPadding,
          width: bubble.width + svgPadding * 2,
          height: bubble.height + svgPadding * 2,
          overflow: "visible",
          pointerEvents: "none",
          zIndex: 7,
        }}
      >
        <g transform={`translate(${svgPadding}, ${svgPadding})`}>
          {/* Bubble shape fill + stroke */}
          <path
            d={bubblePath}
            fill={bgColor}
            stroke={borderColor}
            strokeWidth={strokeW}
            strokeLinejoin="round"
            strokeDasharray={shapeDef.strokeDasharray || undefined}
          />
          {/* Double outline */}
          {shapeDef.doubleOutline && (
            <path
              d={shapeDef.getPath(bubble.width - 6, bubble.height - 6)}
              fill="none"
              stroke={borderColor}
              strokeWidth={strokeW}
              transform="translate(3, 3)"
            />
          )}

          {/* Render all tails */}
          {tailEnabled && tailTriangles.map((tailData, index) => renderTail(tailData, index))}
        </g>
      </svg>

      {/* Text content */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent:
            textAlign === "center"
              ? "center"
              : textAlign === "right"
                ? "flex-end"
                : "flex-start",
          textAlign,
          fontSize,
          fontFamily,
          fontWeight,
          fontStyle,
          textTransform,
          lineHeight: 1.4,
          cursor: "text",
          zIndex: 13,
          padding: 7,
          boxSizing: "border-box",
          color: textColor,
        }}
        onClick={() => {
          setIsEditing(true);
          onSelect && onSelect();
        }}
      >
        {isEditing ? (
          <textarea
            autoFocus
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            onBlur={handleTextCommit}
            onFocus={(event) => {
              event.stopPropagation();
              onSelect && onSelect();
            }}
            style={{
              width: "100%",
              height: "100%",
              resize: "none",
              // Lettering guide: a non-photo-blue box, like pencilled guidelines
              border: "1px dashed #00a4e4",
              borderRadius: 0,
              outline: "none",
              background: "transparent",
              padding: 6,
              fontSize,
              fontFamily,
              fontWeight,
              fontStyle,
              textTransform,
              boxSizing: "border-box",
              color: textColor,
            }}
          />
        ) : (
          <span style={{ color: draftText ? "inherit" : "#6b7280" }}>
            {draftText || "Click to add text"}
          </span>
        )}
      </div>

      {showControls && !bubble.locked && (
        <>
          {edges.map((edge) => (
            <BubbleResizeHandle key={edge} edge={edge} onPointerDown={(e) => startResize(edge, e)} />
          ))}
        </>
      )}

      {showControls && !bubble.locked && tailEnabled && tails.map((tail, index) => (
        <TailDragHandle
          key={tail.id || `tail-${index}`}
          x={tail.tipX}
          y={tail.tipY}
          onPointerDown={(e) => startTailDrag(index, e)}
        />
      ))}
    </div>
  );
};

export default SpeechBubble;
